/* ===========================================================================
 * cro-guide.js — companion to the /pages/how-it-works sections
 *   (section-cro-hero, section-cro-stages, section-cro-system)
 *
 * Three jobs, each of which stands down when assets/cro-deck.js has taken the
 * page over as a slide deck — it knows exactly when a section arrives, which
 * an observer watching a scroll position can only approximate:
 *   1. Play each stage's animation once, when that stage arrives. Under the
 *      deck this listens for the deck's own `cro:slide` event; otherwise it
 *      watches the scroll with an IntersectionObserver.
 *   2. Transition panels and animations in and out. Deck only: the deck's CSS
 *      does this off the slide state, so this half is skipped entirely.
 *   3. Keep the header's active-link underline on the stage being read. Deck
 *      only: it does this itself, so this half is skipped too.
 *
 * LOOPING IS A CROSSFADE, NOT THE `loop` ATTRIBUTE. Four of the six clips do
 * not end where they began — measured on 64px greyscale, the ecosystem clip's
 * first and last frames differ by 6.0 mean levels and newborn by 5.7 — so a
 * plain loop snaps back visibly. Instead the clip fades out at the end, is
 * rewound behind the fade, and fades back in. The seam is hidden rather than
 * avoided, which is the only option with renders that were not authored to
 * loop.
 *
 * The stage clips Casey re-delivered in September 2026 ARE authored to loop:
 * each flies the product in from an empty field and back out to one. The
 * crossfade stays (it fades an empty frame into an empty frame, so it costs
 * nothing), and data-cro-loop-start sends each loop back to 0 so the fly-in
 * plays again, while the first play still starts where the poster is.
 *
 * It also solves the intro clip's opposite problem. That one is the home
 * page's hero, which opens on a deliberately empty frame so the product can
 * slide in; restarting at 0 would flash an empty panel. It carries
 * data-cro-start, so every loop begins where the product is already there.
 *
 * Nothing is fetched until a stage is near: the six clips come to ~2.4MB and
 * only the hero's is needed to start with.
 *
 * Fail-open. Every clip ships with a real poster frame of its own artwork, so
 * a browser that never runs this script shows six stills rather than six
 * empty boxes. Under prefers-reduced-motion it deliberately does not run at
 * all, which leaves exactly those stills.
 *
 * Safe to load more than once — the theme editor re-renders sections — since
 * every element it touches is marked as initialised.
 * ======================================================================== */
(function () {
  'use strict';

  var REDUCED = false;
  try {
    REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  /* Autoplay can be refused outright (iOS low power mode, data saver). The
   * poster is a perfectly good still when it is, so the rejection is
   * swallowed rather than treated as an error. */
  function play(video) {
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
  }

  function startAt(video) {
    return parseFloat(video.getAttribute('data-cro-start')) || 0;
  }

  /* Where a loop restarts. The stage clips fly the product in from an empty
   * frame and out to one, so they are authored to loop: the first play starts
   * where the product has landed (matching the poster), but each loop goes
   * back to 0 and flies it in again. Clips without the attribute loop from
   * their start point, as the intro always has. */
  function loopStartAt(video) {
    var value = video.getAttribute('data-cro-loop-start');
    return value === null ? startAt(video) : parseFloat(value) || 0;
  }

  /* Seek back to the clip's start point, or to `time` if given. Wrapped
   * because assigning currentTime before the browser has anything seekable
   * throws. */
  function rewind(video, time) {
    try {
      video.currentTime = time === undefined ? startAt(video) : time;
    } catch (e) {}
  }

  /* The loop, as a crossfade. `ended` fires, the clip fades out on the CSS
   * transition attached to .is-recycling, and the rewind and the restart
   * happen inside that fade so neither is ever seen. FADE must stay in step
   * with the transition on .cro-clip in cro-guide.css. */
  var FADE = 260;

  function loopWithFade(video) {
    if (video.dataset.croLooping === 'true') return;
    video.dataset.croLooping = 'true';

    video.addEventListener('ended', function () {
      video.classList.add('is-recycling');
      setTimeout(function () {
        rewind(video, loopStartAt(video));
        play(video);

        /* Fade back in one frame after the restart, not in the same tick:
         * clearing the class alongside the seek lets the browser paint the
         * rewound frame before the fade has finished, which is the snap this
         * whole dance exists to hide.
         *
         * Belt and braces, because the consequence of never clearing it is an
         * animation stuck at opacity 0. A backgrounded tab is served no
         * animation frames at all, so a clip that ends while the reader is
         * elsewhere would come back invisible. Whichever of the two fires
         * first wins; the other finds the class already gone. */
        var show = function () {
          video.classList.remove('is-recycling');
        };
        requestAnimationFrame(function () {
          requestAnimationFrame(show);
        });
        setTimeout(show, 120);
      }, FADE);
    });
  }

  function hydrate(video) {
    if (video.dataset.croHydrated === 'true') return;
    video.dataset.croHydrated = 'true';

    var sources = video.querySelectorAll('source[data-src]');
    if (!sources.length) return;

    for (var i = 0; i < sources.length; i++) {
      sources[i].setAttribute('src', sources[i].getAttribute('data-src'));
    }

    loopWithFade(video);

    /* A clip with a start offset has to be put there once metadata lands,
     * or its poster and its first painted frame disagree. */
    if (startAt(video)) {
      video.addEventListener('loadedmetadata', function () {
        rewind(video);
      });
    }

    video.load();
  }

  function deckOn() {
    return document.documentElement.classList.contains('cro-deck-on');
  }

  /* Under the deck, an IntersectionObserver is useless for this: every slide
   * is absolutely positioned at inset 0, so all six are "intersecting" all of
   * the time and all six clips would play at once, off screen. The deck says
   * which slide is live, so play that one and rewind the rest. */
  function initDeckVideos() {
    var videos = [].slice.call(document.querySelectorAll('[data-cro-video]'));
    for (var i = 0; i < videos.length; i++) hydrate(videos[i]);

    function sync() {
      for (var i = 0; i < videos.length; i++) {
        var video = videos[i];
        var slide = video.closest('.cro-slide');
        if (slide && slide.classList.contains('is-active')) {
          video.classList.remove('is-recycling');
          rewind(video);
          play(video);
        } else if (!video.paused) {
          video.pause();
        }
      }
    }

    document.addEventListener('cro:slide', sync);
    sync();
  }

  function initVideos() {
    if (deckOn()) return initDeckVideos();

    var videos = document.querySelectorAll('[data-cro-video]');
    if (!videos.length) return;

    /* No observer to tell us when a stage is near: hydrate everything and let
     * the clips sit on their first frame. Better a still page than 2.4MB that
     * never plays. */
    if (!('IntersectionObserver' in window)) return;

    /* Two observers with different margins, on purpose. The loader fires
     * early (300px out) so the first frames are decoded by the time a stage
     * is on screen; the player fires only on real visibility, so a clip is
     * never spent while it is off screen. One observer cannot do both — an
     * early trigger would play the animation to someone who has not reached
     * it yet, and a late one would show an undecoded frame. */
    var loader = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          hydrate(entry.target);
          loader.unobserve(entry.target);
        });
      },
      { rootMargin: '300px 0px' }
    );

    var player = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;

          if (entry.isIntersecting) {
            hydrate(video);
            /* Rewind before playing, so returning to a stage replays its
             * transformation from the beginning rather than doing nothing
             * because the clip already ran to its end. */
            if (video.dataset.croSeen === 'true') {
              video.classList.remove('is-recycling');
              rewind(video);
            }
            video.dataset.croSeen = 'true';
            play(video);
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.35 }
    );

    for (var i = 0; i < videos.length; i++) {
      if (videos[i].dataset.croObserved === 'true') continue;
      videos[i].dataset.croObserved = 'true';
      loader.observe(videos[i]);
      player.observe(videos[i]);
    }
  }

  /* ---------------------------------------------------------------------
   * Header scroll-spy
   *
   * The CRO header's links used to target frames of a scroll-scrubbed
   * sequence, with the maths in cro-explode-video.js. They are plain
   * `#anchor` links now, so all that is left is keeping the underline on
   * whichever stage is in view.
   *
   * "Most visible wins" rather than "last one to cross": stages are tall
   * enough that two can be partly on screen at once on a laptop, and the
   * latter flickers between two links when you stop scrolling on a boundary.
   * ------------------------------------------------------------------- */
  function initScrollSpy() {
    if (deckOn()) return; // the deck sets this from the slide it moves to
    var nav = document.querySelector('[data-cro-nav]');
    if (!nav || !('IntersectionObserver' in window)) return;
    if (nav.dataset.croSpy === 'true') return;
    nav.dataset.croSpy = 'true';

    var links = nav.querySelectorAll('a[href^="#"]');
    if (!links.length) return;

    var byId = {};
    var targets = [];

    for (var i = 0; i < links.length; i++) {
      var id = links[i].getAttribute('href').slice(1);
      if (!id) continue;
      var target = document.getElementById(id);
      if (!target) continue;
      byId[id] = links[i];
      targets.push(target);
    }
    if (!targets.length) return;

    var ratios = {};

    function paint() {
      var best = null;
      var bestRatio = 0;
      for (var id in ratios) {
        if (ratios[id] > bestRatio) {
          bestRatio = ratios[id];
          best = id;
        }
      }
      for (var key in byId) {
        var on = key === best && bestRatio > 0;
        byId[key].classList.toggle('is-active', on);
        if (on) {
          byId[key].setAttribute('aria-current', 'true');
        } else {
          byId[key].removeAttribute('aria-current');
        }
      }
    }

    var spy = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          ratios[entry.target.id] = entry.isIntersecting ? entry.intersectionRatio : 0;
        });
        paint();
      },
      { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
    );

    for (var t = 0; t < targets.length; t++) spy.observe(targets[t]);
  }

  /* ---------------------------------------------------------------------
   * Entrance transitions
   *
   * Each panel and each animation settles as its section takes the screen,
   * and lifts again on the way out, so the snapped sections read as arriving
   * rather than as a wall that happens to stop in the right place.
   *
   * FAIL-OPEN, and the ordering here is what makes it so. The CSS hides
   * nothing until `.cro-guide--live` is on the page, and that class only goes
   * on once this function has run and is holding the observer. A script that
   * never loads, or a browser without IntersectionObserver, therefore leaves
   * every section fully visible instead of stranding the whole page at
   * opacity 0 — the failure that is worth designing against, because it takes
   * the content with it.
   *
   * Toggled rather than one-shot: with full-height snapping you scroll back
   * up these sections as often as you scroll down them.
   * ------------------------------------------------------------------- */
  function initReveals() {
    if (REDUCED) return;
    if (deckOn()) return; // the deck's CSS animates off the slide state instead
    if (!('IntersectionObserver' in window)) return;

    /* A hidden tab — opened in a background tab, prerendered, restored from a
     * session — is served no observer callbacks at all until it is first
     * looked at. Arming the hiding there would leave the whole page blank
     * behind a tab the reader has not switched to yet. The same check guards
     * the home page reveals in layout/theme.liquid, for the same reason. */
    try {
      if (document.visibilityState === 'hidden') return;
    } catch (e) {
      return;
    }

    var targets = document.querySelectorAll('[data-cro-reveal]');
    if (!targets.length) return;

    var root = document.documentElement;

    /* The dead man's switch. Everything above proves the script is running,
     * but not that the observer will ever deliver — and the failure mode if
     * it does not is the entire page at opacity 0, which is far worse than no
     * animation. So the hiding is released on a timer that the first real
     * callback cancels. Anything already on screen fires well inside this. */
    var release = setTimeout(function () {
      root.classList.remove('cro-guide--live');
    }, 2000);

    var io = new IntersectionObserver(
      function (entries) {
        clearTimeout(release);
        entries.forEach(function (entry) {
          entry.target.classList.toggle('is-inview', entry.isIntersecting);
        });
      },
      /* A low threshold with a negative bottom margin: the element counts as
       * arrived once a little of it is up from the bottom edge, which lands
       * the transition mid-scroll rather than after the snap has finished. */
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    for (var i = 0; i < targets.length; i++) io.observe(targets[i]);

    root.classList.add('cro-guide--live');
  }

  function init() {
    if (!REDUCED) initVideos();
    initReveals();
    initScrollSpy();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
